import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Languages, Loader2, Sparkles } from "lucide-react";
import { translateText } from "../services/gemini";

// Shows a technical text (e.g. clinical instructions) with an on-demand AI translation.
// The stored original is NEVER overwritten — the translation is rendered as a separate
// block below it. Only the provided (non-PHI) text is sent to Gemini.
export const TranslatableText = ({ text, context = "dental CAD clinical instructions", testid = "translatable" }) => {
  const { t, i18n } = useTranslation();
  const target = (i18n.language || "en").toLowerCase().startsWith("es") ? "es" : "en";
  const [busy, setBusy] = useState(false);
  const [translated, setTranslated] = useState(null); // { text, lang }
  const [show, setShow] = useState(false);

  if (!text || !String(text).trim()) return null;

  const doTranslate = async () => {
    // Re-use cached translation if it matches the current UI language.
    if (translated && translated.lang === target) { setShow(true); return; }
    setBusy(true);
    try {
      const res = await translateText(text, target);
      if (res?.status === "ok" && res.translated) {
        setTranslated({ text: res.translated, lang: target });
        setShow(true);
      } else {
        toast.error(t("case.translateFailed"));
      }
    } catch {
      toast.error(t("case.translateFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t("case.original")}</p>
        {!show ? (
          <button
            type="button"
            onClick={doTranslate}
            disabled={busy}
            data-testid={`${testid}-translate-btn`}
            className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-brand transition-colors hover:bg-brand/10 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />}
            {t("case.aiTranslate")}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setShow(false)}
            data-testid={`${testid}-hide-btn`}
            className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-secondary"
          >
            {t("case.hideTranslation")}
          </button>
        )}
      </div>
      <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-foreground" data-testid={`${testid}-original`}>{text}</p>

      {show && translated && (
        <div className="mt-3 rounded-md border border-brand/30 bg-brand/5 p-3" data-testid={`${testid}-ai-block`}>
          <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-brand">
            <Sparkles className="h-3 w-3" /> {t("case.aiTranslation")}
          </p>
          <p className="whitespace-pre-line text-sm leading-relaxed text-foreground" data-testid={`${testid}-translated`}>{translated.text}</p>
        </div>
      )}
    </div>
  );
};
